import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, styled } from "@mui/material";
import { Mail, Lock } from "lucide-react";
import { useAuth } from "@Auth/useAuth";
import { AuthClient } from "@Clients/AuthClient";
import { Icon } from "@assets/Icon";
import { Button, Checkbox, Field } from "@ui";
import { onErrorShow } from "@utils/onErrorShow";
import {
  AuthCard,
  AuthDivider,
  AuthFooterText,
  AuthLink,
} from "./authPrimitives";
import { validateEmail, validatePassword } from "../utils/authValidation";

/**
 * The login form card: social + email/password sign-in and password reset.
 * Self-contained (owns its form state); the page composes it inside the auth
 * layout, with the trust badges rendered just below it.
 */
export function LoginForm() {
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>(
    {}
  );

  const handleGoogle = async () => {
    setGoogleLoading(true);
    try {
      const result = await signIn();
      if (result?.error) {
        onErrorShow(
          result.error instanceof Error
            ? result.error.message
            : "Couldn't sign in with Google"
        );
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const validate = () => {
    const next = {
      email: validateEmail(email),
      password: validatePassword(password),
    };
    setErrors(next);
    return !next.email && !next.password;
  };

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await AuthClient.loginWithPassword(email, password);
      navigate("/products/all-products");
    } catch (error) {
      onErrorShow(error instanceof Error ? error.message : "Couldn't sign in");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthCard as="form" onSubmit={handleLogin}>
      <Button
        variant="secondary"
        size="lg"
        fullWidth
        startIcon={<Icon name="google" />}
        loading={googleLoading}
        onClick={handleGoogle}
      >
        Continue with Google
      </Button>

      <AuthDivider />

      <Field
        label="Email"
        type="email"
        autoComplete="email"
        placeholder="you@example.com"
        leadingIcon={<Mail size={18} />}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={errors.email}
      />
      <Field
        label="Password"
        type="password"
        autoComplete="current-password"
        placeholder="Enter your password"
        leadingIcon={<Lock size={18} />}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={errors.password}
      />

      <Row>
        <Checkbox checked={remember} onChange={setRemember} label="Remember me" />
        <AuthLink type="button" onClick={() => navigate("/forgot-password")}>
          Forgot password?
        </AuthLink>
      </Row>

      <Button type="submit" size="lg" fullWidth loading={loading}>
        Sign in
      </Button>

      <AuthFooterText>
        Don't have an account?{" "}
        <AuthLink type="button" onClick={() => navigate("/sign-up")}>
          Sign up
        </AuthLink>
      </AuthFooterText>
    </AuthCard>
  );
}

const Row = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
});
